/** Comparar en 3D (ADR 0012, parte C): dos productos con 3D parados uno al lado del otro a escala real, separados 40 cm, en
 * UN visor; cada uno con su color elegido, sus cotas (ancho, alto, fondo de los metafields) y su nombre y precio abajo.
 * La escena se compone en el cliente (assets/glb.js: se parsean los dos GLB, se fija la variante de color y se juntan los
 * nodos con la traslación en x) y se pasa a model-viewer como Blob. Debajo, tabla con los datos reales de los metafields y
 * «Agregar» por producto (quick-buy). Sin WebGL o con movimiento reducido: los dos pósters lado a lado, sin visor.
 * Estado en la URL: ?a=handle[:variante]&b=handle[:variante] (compartible; las tarjetas y la ficha llegan con esos parámetros).
 * AR: WebXR y Quick Look (model-viewer exporta el USDZ de la escena en memoria); Scene Viewer no abre un GLB sin URL
 * pública, así que no se ofrece (ADR 0012). */
const GAP = 0.4;
const SLOTS = ['a', 'b'];

class Compare3d extends HTMLElement {
  async connectedCallback() {
    if (this.data) return;
    try { this.data = JSON.parse(this.querySelector('[data-compare-data]')?.textContent || '{}'); } catch { this.data = {}; }
    this.products = this.data.products || [];
    this.strings = this.data.strings || {};
    this.stage = this.querySelector('[data-stage]');
    this.canvas = this.querySelector('[data-canvas]');
    this.svg = this.querySelector('[data-svg]');
    this.labels = this.querySelector('[data-labels]');
    this.empty = this.querySelector('[data-empty]');
    this.staticBox = this.querySelector('[data-static]');
    this.table = this.querySelector('[data-table]');
    this.live = this.querySelector('[data-live]');
    if (!this.products.length) { this.toggleAttribute('data-none', true); return; }
    this.state = this.fromUrl();
    this.renderPickers();
    this.addEventListener('change', (event) => this.onChange(event));
    this.scene = await import(this.dataset.scene);
    this.has3d = this.scene.canShow3d();
    this.classList.toggle('compare--static', !this.has3d);
    this.update(true);
  }

  product(handle) { return this.products.find((p) => p.handle === handle) || null; }
  variantOf(product, title) { return product?.variants.find((v) => v.title === title) || product?.variants.find((v) => v.available) || product?.variants[0] || null; }

  fromUrl() {
    const params = new URLSearchParams(window.location.search);
    const state = {};
    const used = [];
    for (const slot of SLOTS) {
      const raw = params.get(slot) || '';
      const [handle, ...rest] = raw.split(':');
      let product = this.product(handle);
      if (!product) product = this.products.find((p) => !used.includes(p.handle)) || this.products[0];
      used.push(product.handle);
      state[slot] = { handle: product.handle, variant: this.variantOf(product, rest.join(':'))?.title || '' };
    }
    return state;
  }

  toUrl() {
    const url = new URL(window.location.href);
    for (const slot of SLOTS) {
      const { handle, variant } = this.state[slot];
      url.searchParams.set(slot, variant ? `${handle}:${variant}` : handle);
    }
    window.history.replaceState(window.history.state, '', url);
  }

  renderPickers() {
    for (const slot of SLOTS) {
      const box = this.querySelector(`[data-slot="${slot}"]`);
      const chips = box.querySelector('[data-chips]');
      chips.replaceChildren(...this.products.map((p) => {
        const label = document.createElement('label');
        label.className = 'compare__chip';
        const input = document.createElement('input');
        input.type = 'radio'; input.name = `compare-${slot}`; input.value = p.handle; input.checked = p.handle === this.state[slot].handle;
        const img = document.createElement('img');
        img.src = p.thumb || ''; img.alt = ''; img.width = 48; img.height = 48; img.loading = 'lazy';
        const name = document.createElement('span'); name.className = 'compare__chip-name'; name.textContent = p.short || p.title;
        label.append(input, img, name);
        return label;
      }));
      this.renderColors(slot);
    }
  }

  renderColors(slot) {
    const box = this.querySelector(`[data-slot="${slot}"] [data-colors]`);
    const product = this.product(this.state[slot].handle);
    const variants = product?.variants || [];
    // La fila reserva su alto (sin salto de layout): sin variantes queda invisible, no se saca.
    box.classList.toggle('is-empty', variants.length < 2);
    box.replaceChildren(...variants.map((v) => {
      const label = document.createElement('label');
      label.className = `swatch${v.available ? '' : ' swatch--off'}`;
      const input = document.createElement('input');
      input.type = 'radio'; input.name = `compare-${slot}-color`; input.value = v.title; input.checked = v.title === this.state[slot].variant;
      const dot = document.createElement('span'); dot.className = 'swatch__dot'; dot.setAttribute('aria-hidden', 'true');
      if (v.dot) dot.style.backgroundImage = `url(${v.dot})`;
      const name = document.createElement('span'); name.className = 'swatch__name'; name.textContent = v.title;
      label.append(input, dot, name);
      return label;
    }));
  }

  onChange(event) {
    const input = event.target;
    const match = input.name?.match(/^compare-([ab])(-color)?$/);
    if (!match) return;
    const slot = match[1];
    if (match[2]) this.state[slot].variant = input.value;
    else {
      const product = this.product(input.value);
      this.state[slot] = { handle: product.handle, variant: this.variantOf(product)?.title || '' };
      this.renderColors(slot);
    }
    this.update(false);
  }

  update(initial) {
    if (!initial) this.toUrl();
    this.renderTable();
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.renderScene(), initial ? 0 : 120);
  }

  /** Los dos productos elegidos con su variante, caja (metros) y textos de cotas. */
  picks() {
    return SLOTS.map((slot) => {
      const product = this.product(this.state[slot].handle);
      const variant = this.variantOf(product, this.state[slot].variant);
      return { slot, product, variant };
    });
  }

  async renderScene() {
    const token = (this.token = (this.token || 0) + 1);
    const picks = this.picks();
    if (!this.has3d) { this.renderStatic(picks); return; }
    // Celular o red limitada: pósters lado a lado y «Tocá para verlos en 3D» hasta el primer toque (ADR 0013).
    if (!this.allowed) {
      this.allowed = this.scene.whenAllowed(this.stage, this.querySelector('[data-compare-start]')).then(() => {
        if (!this.scene.supportsWebGL()) { this.has3d = false; this.classList.add('compare--static'); return; }
        this.go3d = true; this.classList.add('compare--live'); this.renderScene();
      });
    }
    if (!this.go3d) { this.renderStatic(picks); return; }
    this.staticBox.hidden = true;
    const scene = this.scene;
    let glbs;
    try {
      glbs = await Promise.all(picks.map((p) => scene.loadGlb(p.product.src, this.dataset.glb)));
    } catch { this.has3d = false; this.classList.add('compare--static'); this.renderStatic(picks); return; }
    if (token !== this.token) return;
    const glb = await scene.glbTools(this.dataset.glb);
    const widths = glbs.map((g) => glb.boundsOf(g.json)?.size[0] || 1);
    const xs = glb.sideBySide(widths, GAP);
    const composed = glb.compose(picks.map((p, i) => ({ glb: glbs[i], variant: p.variant?.title, instances: [[xs[i], 0, 0]], name: p.slot })));
    if (!(await scene.loadViewer()) || token !== this.token) return;
    if (!this.viewer) {
      // En mobile (visor casi cuadrado) la cámara se aleja para que entren los dos productos con sus cotas.
      const narrow = matchMedia('(max-width: 767px)').matches;
      this.viewer = scene.createViewer({ alt: this.strings.alt || '', orbit: narrow ? '30deg 74deg 118%' : '30deg 74deg 78%', maxRadius: '140%' });
      const ar = document.createElement('button');
      ar.type = 'button'; ar.slot = 'ar-button'; ar.className = 'build__ar'; ar.textContent = this.strings.ar || 'AR';
      this.viewer.append(ar);
      this.canvas.append(this.viewer);
      this.dims = new scene.SceneDims(this.viewer, this.svg, this.labels);
      this.viewer.addEventListener('load', () => this.onSceneLoad(), { passive: true });
    }
    this.pendingScene = { picks, parts: composed.parts };
    scene.setSceneBuffer(this.viewer, composed.buffer);
    this.classList.add('compare--3d');
    this.empty.hidden = true;
  }

  /** Escena cargada: cotas por producto y, debajo del visor, nombre y precio de cada uno alineados con su posición. */
  onSceneLoad() {
    const pending = this.pendingScene;
    if (!pending) return;
    this.pendingScene = null;
    const { picks, parts } = pending;
    const boxes = [];
    picks.forEach((p, i) => {
      const box = parts[i]?.bounds?.[0];
      if (!box) return;
      const dims = p.product.dims || [];
      boxes.push({ key: p.slot, min: box.min, max: box.max, hRight: i === picks.length - 1 && picks.length > 1, texts: { w: dims[0] || '', h: dims[1] || '', d: dims[2] || '' } });
    });
    this.dims.set(boxes);
    if (boxes.length) this.scene.frameLow(this.viewer, { min: [0, 1, 2].map((k) => Math.min(...boxes.map((b) => b.min[k]))), max: [0, 1, 2].map((k) => Math.max(...boxes.map((b) => b.max[k]))) });
    this.renderNames(picks);
    this.viewer.classList.add('is-ready');
    if (this.live) this.live.textContent = (this.strings.live || '').replace('[a]', picks[0].product.title).replace('[b]', picks[1].product.title);
    this.dispatchEvent(new CustomEvent('compare:loaded', { detail: { boxes } }));
  }

  /** Nombre, color y precio de cada producto bajo el visor, en el mismo orden (izquierda, derecha) que en la escena. */
  renderNames(picks) {
    const row = this.querySelector('[data-names]');
    if (!row) return;
    row.replaceChildren(...picks.map((p) => {
      const item = document.createElement('p');
      item.className = 'compare__name';
      const name = document.createElement('span'); name.className = 'compare__name-title'; name.textContent = p.product.short || p.product.title;
      const meta = document.createElement('span'); meta.className = 'compare__name-price';
      meta.textContent = [p.variant?.title && p.product.variants.length > 1 ? p.variant.title : '', this.priceText(p)].filter(Boolean).join(' · ');
      item.append(name, meta);
      return item;
    }));
    row.hidden = false;
  }

  priceText(p) {
    const s = this.strings;
    if (p.product.state === 'cotizar') return s.quote || '';
    if (p.product.state === 'proximamente') return s.coming || '';
    return p.variant?.price || '';
  }

  /** Sin 3D: los dos pósters armada (del color elegido) lado a lado, a la misma escala real por ancho. */
  renderStatic(picks) {
    this.empty.hidden = true;
    this.staticBox.hidden = false;
    this.renderNames(picks);
    const widths = picks.map((p) => Number(p.product.width_m) || 1);
    const total = widths.reduce((a, b) => a + b, 0) + GAP;
    this.staticBox.replaceChildren(...picks.map((p, i) => {
      const fig = document.createElement('figure');
      fig.className = 'compare__static-item';
      fig.style.flexBasis = `${((widths[i] / total) * 100).toFixed(1)}%`;
      const img = document.createElement('img');
      const url = (p.product.posters && p.variant && p.product.posters[p.variant.title]) || p.product.poster || '';
      // Es lo más grande de la página antes del 3D (el LCP): carga ya y del tamaño justo (CDN de imágenes con width=, ADR 0013).
      if (/\/cdn\/shop\//.test(url)) {
        const at = (w) => `${url.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '')}${url.includes('?') ? '&' : '?'}width=${w}`;
        img.srcset = [360, 480, 720, 960, 1280].map((w) => `${at(w)} ${w}w`).join(', ');
        img.sizes = `(min-width: 1024px) ${Math.round(((widths[i] / total) * 100) * 0.9)}vw, ${Math.round((widths[i] / total) * 100)}vw`;
      }
      img.src = url;
      img.alt = p.product.title; img.width = 1600; img.height = 1300; img.decoding = 'async';
      if (i === 0) img.fetchPriority = 'high';
      // Nombre y precio debajo de cada póster: el CSS los muestra solo en modo estático (ahí la fila de nombres no va).
      const cap = document.createElement('figcaption');
      cap.textContent = `${p.product.short || p.product.title} · ${this.priceText(p)}`;
      fig.append(img, cap);
      return fig;
    }));
  }

  renderTable() {
    const s = this.strings;
    const picks = this.picks();
    const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
    const thead = el('thead');
    const hr = el('tr');
    hr.append(el('th', 'compare__th-label', ''));
    picks.forEach((p) => { const th = el('th', 'compare__th'); th.scope = 'col'; const a = el('a', 'compare__link', p.product.short || p.product.title); a.href = p.product.url; a.title = p.product.title; th.append(a); hr.append(th); });
    thead.append(hr);
    const tbody = el('tbody');
    const rows = [
      [s.row_medidas, (p) => p.product.medidas],
      [s.row_niveles, (p) => p.product.niveles],
      [s.row_carga, (p) => p.product.carga],
      [s.row_precio, (p) => this.priceText(p)],
      [s.row_entrega, (p) => (p.product.state === 'preventa' ? (p.product.arrival ? (s.preventa_date || '').replace('[date]', p.product.arrival) : s.preventa) : p.product.state === 'proximamente' ? (s.coming_date || '').replace('[date]', p.product.arrival || '') : s.in_stock)],
    ];
    for (const [label, get] of rows) {
      const values = picks.map(get);
      if (values.every((v) => !v)) continue;
      const tr = el('tr');
      const th = el('th', 'compare__row', label); th.scope = 'row';
      tr.append(th);
      values.forEach((v) => tr.append(el('td', null, v || '—')));
      tbody.append(tr);
    }
    const tr = el('tr', 'compare__actions');
    tr.append(el('th', 'compare__row', ''));
    picks.forEach((p) => {
      const td = el('td');
      const v = p.variant;
      if (p.product.state === 'proximamente') {
        const a = el('a', 'button button--secondary', s.notify); a.href = `${p.product.url}#AvisoLlegada-main`; td.append(a);
      } else if (p.product.state === 'cotizar') {
        const a = el('a', 'button button--secondary', s.quote); a.href = p.product.url; td.append(a);
      } else if (v) {
        const quick = document.createElement('quick-buy');
        quick.dataset.error = s.error || ''; quick.dataset.adding = s.adding || ''; quick.dataset.added = s.added || ''; quick.dataset.soldOut = s.sold_out || '';
        const form = document.createElement('form');
        form.action = this.dataset.cartAdd; form.method = 'post';
        const id = document.createElement('input'); id.type = 'hidden'; id.name = 'id'; id.value = String(v.id);
        const qty = document.createElement('input'); qty.type = 'hidden'; qty.name = 'quantity'; qty.value = '1';
        form.append(id, qty);
        if (p.product.delivery) { const prop = document.createElement('input'); prop.type = 'hidden'; prop.name = `properties[${p.product.delivery.key}]`; prop.value = p.product.delivery.value; form.append(prop); }
        const button = el('button', 'button', v.available ? s.add : s.sold_out); button.type = 'submit'; button.disabled = !v.available;
        const status = el('p', 'quick-buy__status'); status.setAttribute('role', 'status');
        form.append(button); quick.append(form, status);
        td.append(quick);
      }
      tr.append(td);
    });
    tbody.append(tr);
    this.table.replaceChildren(thead, tbody);
  }
}
if (!customElements.get('compare-3d')) customElements.define('compare-3d', Compare3d);
